export default `vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p, int octaves) {
  float sum = 0.0;
  float amp = 0.5;
  float freq = 1.0;
  for (int i = 0; i < 8; i++) {
    if (i >= octaves) break;
    sum += amp * snoise(p * freq);
    freq *= 2.0;
    amp *= 0.5;
  }
  return sum;
}

vec3 snoiseVec3(vec3 p) {
  return vec3(snoise(p), snoise(p + 19.19), snoise(p + 113.7));
}

vec3 curlNoise(vec3 p) {
  const float e = 0.1;
  vec3 dx = vec3(e, 0.0, 0.0);
  vec3 dy = vec3(0.0, e, 0.0);
  vec3 dz = vec3(0.0, 0.0, e);

  vec3 px0 = snoiseVec3(p - dx), px1 = snoiseVec3(p + dx);
  vec3 py0 = snoiseVec3(p - dy), py1 = snoiseVec3(p + dy);
  vec3 pz0 = snoiseVec3(p - dz), pz1 = snoiseVec3(p + dz);

  float x = (py1.z - py0.z) - (pz1.y - pz0.y);
  float y = (pz1.x - pz0.x) - (px1.z - px0.z);
  float z = (px1.y - px0.y) - (py1.x - py0.x);

  return normalize(vec3(x, y, z) / (2.0 * e) + 1e-6);
}

uniform float uTime;
uniform float uElevationScale;
uniform float uNoiseFrequency;
uniform int   uNoiseOctaves;
uniform float uPathValleyDepth;
uniform float uPathValleyWidth;
uniform float uWaveAmount; 
uniform float uWaveAmp;
uniform float uWaveFreq;
uniform float uWaveSpeed;
uniform float uStrataAmount; 
uniform float uStrataStep;
uniform float uFlatten;       
uniform vec2  uFlattenCenter; 
uniform float uFlattenRadius;
uniform float uFlattenLevel;  
uniform float uRipple;        
uniform vec2  uRippleCenter;
uniform float uRippleFreq;
uniform float uRippleSpeed;
uniform float uRippleAmp;
uniform float uAudioBass;     
uniform vec2  uPathPoints[64];
uniform int   uPathCount;
uniform float uReveal; 

varying vec3  vWorldPos;
varying float vElevation;
varying float vPathDist;

float distToPath(vec2 p) {
  float d = 1e9;
  for (int i = 0; i < 63; i++) {
    if (i >= uPathCount - 1) break;
    vec2 a = uPathPoints[i];
    vec2 b = uPathPoints[i + 1];
    vec2 pa = p - a;
    vec2 ba = b - a;
    float t = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-5), 0.0, 1.0);
    d = min(d, length(pa - ba * t));
  }
  return d;
}

void main() {
  vec3 pos = position;          
  vec2 xz = pos.xz;

  
  
  float e = fbm(vec3(xz * uNoiseFrequency, uTime * 0.02), uNoiseOctaves);
  e = e * 0.5 + 0.5;            
  float elev = e * uElevationScale;

  float pd = distToPath(xz);
  vPathDist = pd;
  float valley = (1.0 - smoothstep(0.0, uPathValleyWidth, pd)) * uPathValleyDepth;
  elev -= valley;

  
  float strataE = floor(elev / uStrataStep) * uStrataStep;
  elev = mix(elev, strataE, uStrataAmount);

  
  
  float wsum =
      sin(xz.x * uWaveFreq + uTime * uWaveSpeed) * 0.5
    + sin(xz.y * uWaveFreq * 0.8 - uTime * uWaveSpeed * 0.8) * 0.5
    + fbm(vec3(xz * uWaveFreq * 0.6 + vec2(0.0, uTime * uWaveSpeed * 0.3), 0.0), 3);
  float waveElev = wsum * uElevationScale * uWaveAmp;
  elev = mix(elev, waveElev, uWaveAmount);

  
  
  float fdist = distance(xz, uFlattenCenter);
  float fmask = (1.0 - smoothstep(uFlattenRadius * 0.55, uFlattenRadius, fdist)) * uFlatten;
  elev = mix(elev, uFlattenLevel, fmask);

  
  
  if (uRipple > 0.001) {
    float rd = distance(xz, uRippleCenter);
    float ring = sin(rd * uRippleFreq - uTime * uRippleSpeed) * exp(-rd * 0.025);
    elev += ring * uRippleAmp * (0.3 + 1.2 * uAudioBass) * uRipple;
  }

  
  
  vElevation = elev;
  pos.y += elev * uReveal;

  vec4 world = modelMatrix * vec4(pos, 1.0);
  vWorldPos = world.xyz;

  gl_Position = projectionMatrix * viewMatrix * world;
}`